/**
 * Typed client for api.studiobereikbaar.nl.
 *
 * Wraps:
 *   - OGC API Processes execution (POST /oapi/processes/{id}/execution)
 *   - Vierkantjes user-saved projects sibling (/vierkantjes/projects/*)
 *
 * Frontend pages call this instead of the legacy sb-platform /api/* REST
 * surface. Mirrors the pattern of lib/nrm-api.ts and lib/traffic-api.ts —
 * direct fetch against the api.sb.nl host (CORS already enabled there).
 */

const BASE_URL =
  (import.meta.env.VITE_SB_API_BASE as string | undefined) ??
  "https://api.studiobereikbaar.nl";

// ---------------------------------------------------------------------------
// OGC Processes
// ---------------------------------------------------------------------------

export interface ProcessInputs {
  [key: string]: unknown;
}

/**
 * Execute an OGC API Process synchronously and return the JSON result.
 * Throws on non-2xx response.
 */
export async function executeProcess<TResult = unknown>(
  processId: string,
  inputs: ProcessInputs,
): Promise<TResult> {
  const url = `${BASE_URL}/oapi/processes/${encodeURIComponent(processId)}/execution`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Prefer: "respond-sync",
    },
    body: JSON.stringify({ inputs }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`OGC ${processId} ${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()) as TResult;
}

// ---------------------------------------------------------------------------
// Modal Split (Vierkantjes) — wraps modal-split-analysis
// ---------------------------------------------------------------------------

/**
 * Which side(s) of the trip the geographic filter applies to.
 *  - "vertrek" (default — Susan methodology): pc_dep / mun_dep / prv_dep / prx_dep.
 *    Trips DEPARTING from the area; destinations are aggregated.
 *  - "aankomst": pc_arr / mun_arr / prv_arr / prx_arr. Trips ARRIVING at
 *    the area; origins are aggregated.
 *  - "intern": both endpoints in the area (pc_dep AND pc_arr). Intra-area
 *    trips only — geometry collapses the distance bins to whatever fits inside.
 *  - "vertrek_of_aankomst": either endpoint in the area (pc_dep OR pc_arr).
 *    Every trip that touches the area on at least one end. Most common
 *    "show me everything that interacts with this place" view.
 *
 * Legacy alias: "vertrek_en_aankomst" resolves to "intern" on the backend.
 */
export type FilterAxis =
  | 'vertrek'
  | 'aankomst'
  | 'intern'
  | 'vertrek_of_aankomst';

export interface ModalSplitInputs {
  postcode?: string;
  /**
   * Comma-free array of postcode patterns. Each entry may be exact
   * ('1012') or trailing-X wildcard ('12XX' = 1200-1299, '1XXX' =
   * 1000-1999). Mix freely.
   */
  postcodes?: string[];
  municipality?: string;
  municipalities?: string[];
  province?: string;
  provincies?: string[];
  stedelijkheid?: string;
  title?: string;
  year_min?: number;
  year_max?: number;
  /** Trip purposes to keep ('Werken', 'Onderwijs/cursus volgen', ...). */
  motives?: string[];
  /** HHMM lower bound on time_dep (e.g. 700 = 07:00 inclusive). */
  time_dep_min?: number;
  /** HHMM upper bound on time_dep (e.g. 900 = 09:00 inclusive). */
  time_dep_max?: number;
  /**
   * Which side of the trip the geo filter applies to. Defaults to
   * 'vertrek' (Susan methodology) when omitted.
   */
  filter_axis?: FilterAxis;
}

export interface CellsReportEntry {
  mode: string;
  distance: string;
  n_trips: number;
  pct_of_total: number;
}

export interface ModalSplitResult {
  /** Base64-encoded PNG bytes (no data: prefix) */
  image: string;
  total_trips: number;
  /**
   * Per-cell sample breakdown — trip count + share for every (mode × distance)
   * cell on the marimekko. Lets a consultant judge whether the sample is
   * large enough to defend the percentages on the chart for a given client.
   */
  cells_report?: CellsReportEntry[];
  /** Trips per mode summed across all distance bins. */
  by_mode?: Record<string, number>;
  /** Trips per distance bin summed across all modes. */
  by_distance?: Record<string, number>;
}

export async function generateModalSplit(
  inputs: ModalSplitInputs,
): Promise<ModalSplitResult> {
  return executeProcess<ModalSplitResult>(
    "modal-split-analysis",
    inputs as unknown as ProcessInputs,
  );
}

/** Convert a base64 PNG into a data URL for <img src=…>. */
export function pngBase64ToDataUrl(b64: string): string {
  return `data:image/png;base64,${b64}`;
}

/** Convert a base64 PNG into a Blob for upload / download. */
export function pngBase64ToBlob(b64: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: "image/png" });
}

// ---------------------------------------------------------------------------
// ODIN query (rich analytics — modal split + purposes + temporal patterns)
// ---------------------------------------------------------------------------

export interface OdinQueryInputs {
  postcode?: string;
  postcodes?: string[];
  municipality?: string;
  province?: string;
  stedelijkheid?: string;
  transport_mode?: string;
  trip_purpose?: string;
  distance_category?: string;
  location_type?: "departure" | "arrival";
  year_min?: number;
  year_max?: number;
  include_modal_split?: boolean;
  include_purposes?: boolean;
  include_distance?: boolean;
  include_yearly_trends?: boolean;
  include_hourly_pattern?: boolean;
}

export interface OdinSummary {
  total_respondents: number;
  total_trips: number;
  years_covered: number;
  unique_postcodes: number;
  unique_municipalities: number;
  earliest_year: number;
  latest_year: number;
}

export interface OdinDistribution {
  labels: string[];
  values: number[];
  percentages: number[];
}

export interface OdinQueryResult {
  summary: OdinSummary;
  distributions?: {
    mode?: OdinDistribution;
    purpose?: OdinDistribution;
    distance?: OdinDistribution;
  };
  data_coverage?: {
    dataset: string;
    total_trips_in_dataset: number;
    total_respondents_in_dataset: number;
    source_table: string;
    methodology?: string;
  };
}

export async function odinQuery(inputs: OdinQueryInputs): Promise<OdinQueryResult> {
  return executeProcess<OdinQueryResult>(
    "odin-query",
    inputs as unknown as ProcessInputs,
  );
}

// ---------------------------------------------------------------------------
// Vierkantjes user-saved projects
// ---------------------------------------------------------------------------

export interface VierkantjesProject {
  id: string;
  title: string;
  source_type: string;
  parameters: Record<string, unknown> | null;
  output_url: string;
  display_url: string;
  download_url: string;
  total_trips: number | null;
  created_at: string | null;
  owner_id: string;
  owner_email: string | null;
  owner_name: string | null;
}

/** List a user's saved Vierkantjes projects, newest first. */
export async function listProjects(
  ownerId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<VierkantjesProject[]> {
  const params = new URLSearchParams({ owner_id: ownerId });
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.offset) params.set("offset", String(opts.offset));
  const url = `${BASE_URL}/vierkantjes/projects?${params.toString()}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`listProjects ${res.status}: ${res.statusText}`);
  return (await res.json()) as VierkantjesProject[];
}

/**
 * Save a generated PNG against the user's project history.
 *
 * Two-step flow: caller has already executed modal-split-analysis (or any
 * other generator), received PNG bytes, and now persists those bytes here.
 */
export async function saveProject(opts: {
  pngBlob: Blob;
  title: string;
  ownerId: string;
  ownerEmail?: string;
  ownerName?: string;
  sourceType?: string;
  parameters?: Record<string, unknown>;
  totalTrips?: number;
}): Promise<VierkantjesProject> {
  const form = new FormData();
  form.append("file", opts.pngBlob, `${opts.title}.png`);
  form.append("title", opts.title);
  form.append("owner_id", opts.ownerId);
  if (opts.ownerEmail) form.append("owner_email", opts.ownerEmail);
  if (opts.ownerName) form.append("owner_name", opts.ownerName);
  if (opts.sourceType) form.append("source_type", opts.sourceType);
  if (opts.parameters) form.append("parameters", JSON.stringify(opts.parameters));
  if (opts.totalTrips !== undefined) form.append("total_trips", String(opts.totalTrips));

  const res = await fetch(`${BASE_URL}/vierkantjes/projects/save`, {
    method: "POST",
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`saveProject ${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()) as VierkantjesProject;
}

export async function deleteProject(projectId: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/vierkantjes/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(`deleteProject ${res.status}: ${res.statusText}`);
  }
}

/** URL for inline display of a saved project's PNG. */
export function projectImageUrl(projectId: string): string {
  return `${BASE_URL}/vierkantjes/projects/${encodeURIComponent(projectId)}/image`;
}

/** URL for forced download of a saved project's PNG. */
export function projectDownloadUrl(projectId: string): string {
  return `${BASE_URL}/vierkantjes/projects/${encodeURIComponent(projectId)}/download`;
}

// ---------------------------------------------------------------------------
// Vierkantjes filter-form metadata (provinces, stedelijkheid, municipalities)
// ---------------------------------------------------------------------------

export interface ProvinceMetadata {
  code: string;
  name: string;
}

export interface StedelijkheidMetadata {
  code: string;
  label: string;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`);
  if (!res.ok) throw new Error(`${path} ${res.status}: ${res.statusText}`);
  return (await res.json()) as T;
}

export async function listProvinces(): Promise<ProvinceMetadata[]> {
  const data = await fetchJson<{ provinces: ProvinceMetadata[] }>(
    "/odin/metadata/provinces",
  );
  return data.provinces;
}

export async function listStedelijkheid(): Promise<StedelijkheidMetadata[]> {
  const data = await fetchJson<{ stedelijkheid: StedelijkheidMetadata[] }>(
    "/odin/metadata/stedelijkheid",
  );
  return data.stedelijkheid;
}

export async function listMunicipalities(): Promise<string[]> {
  const data = await fetchJson<{ municipalities: string[] }>(
    "/odin/metadata/municipalities",
  );
  return data.municipalities;
}

export async function listTransportModes(): Promise<string[]> {
  const data = await fetchJson<{ transport_modes: string[] }>(
    "/odin/metadata/transport-modes",
  );
  return data.transport_modes;
}

export async function listTripPurposes(): Promise<string[]> {
  const data = await fetchJson<{ trip_purposes: string[] }>(
    "/odin/metadata/trip-purposes",
  );
  return data.trip_purposes;
}

// ---------------------------------------------------------------------------
// Accessibility Maps — wraps OGC accessibility-map process + options
// ---------------------------------------------------------------------------

export interface AccessibilityMapInputs {
  mode: string;
  amenity: string;
  region_type: string;
  region_id?: string;
  map_type: string;
  include_amenity_dots?: boolean;
}

export interface AccessibilityMapResult {
  /** Base64-encoded PNG */
  image: string;
  table_name: string;
  metadata: {
    status: string;
    region_label?: string;
    processing_time_ms?: number;
    error_message?: string;
  };
}

export async function generateAccessibilityMap(
  inputs: AccessibilityMapInputs,
): Promise<AccessibilityMapResult> {
  return executeProcess<AccessibilityMapResult>(
    "accessibility-map",
    inputs as unknown as ProcessInputs,
  );
}

export interface AccessibilityMapOptions {
  region_types: Array<{ id: string; label: string; label_en: string; description: string }>;
  provinces: Array<{ id: string; label: string }>;
  municipalities: Array<{ id: string; label: string; statnaam: string }>;
  amenities_by_category: Record<
    string,
    {
      label: string;
      label_en: string;
      amenities: Array<{ id: string; label: string; label_en: string }>;
    }
  >;
  transport_modes: Array<{ id: string; label: string; label_en: string; description: string }>;
  map_types: Array<{
    id: string;
    label: string;
    label_en: string;
    description: string;
    description_en?: string;
  }>;
}

export async function getAccessibilityMapOptions(): Promise<AccessibilityMapOptions> {
  return fetchJson<AccessibilityMapOptions>("/accessibility-maps/options");
}

// ---------------------------------------------------------------------------
// Registry catalog — the single source of truth the docs read their numbers
// from. GET /catalog/datasets and /catalog/processes are public + CORS-open.
// Field names below mirror the live API response verbatim (verified against
// api.studiobereikbaar.nl). Never hardcode counts derived from these; fetch.
// ---------------------------------------------------------------------------

export interface CatalogDatasetMetadata {
  schema_name?: string;
  intended_status?: string;
  domain?: string;
  source_description?: string | null;
  source_url?: string | null;
  refresh_cadence?: string | null;
  methodology_ver?: string | null;
  last_refreshed_at?: string | null;
  built_at?: string | null;
  built_by?: string | null;
  notes?: string | null;
}

export interface CatalogDataset {
  schema: string;
  intended_status: string;
  domain: string;
  table_count: number;
  exists: boolean;
  has_metadata_table: boolean;
  metadata: CatalogDatasetMetadata | null;
  /** Present only when the schema lives in a non-default source database. */
  source_db?: string;
}

export interface CatalogDatasetsResponse {
  summary: {
    /** @deprecated Counts registry ROWS, not schemas — kept for compatibility.
     *  Use total_rows (same number, honest name) or distinct_schemas. */
    total_schemas_tracked: number;
    // The four below arrived with the lane-aware catalog (sb-api PR #25) and
    // are optional so the client still typechecks against an older sb-api.
    /** Registry rows in public.datalake_collections (95 in prod). */
    total_rows?: number;
    /** Distinct source_schema values (74 in prod) — several rows share a
     *  schema: 7 resolve to verkeersmodel_nrm, 5 to netwerk_gtfs_v9437. */
    distinct_schemas?: number;
    with_metadata_table: number;
    /** Genuine drift only. Retired rows are excluded, so 0 means 0. */
    missing_schema: number;
    /** Rows whose physical absence is the intended end-state (deprecated). */
    retired?: number;
    /** Row count per binding lane: postgis | both | parquet | unbound. */
    by_lane?: Record<string, number>;
  };
  datasets: CatalogDataset[];
}

export interface CatalogProcess {
  id: string;
  module: string;
  title: string;
  description: string;
  version: string;
  category: string;
  data_sources: string[];
  methodology: string | null;
  /** Non-null when the process is wired as a Claude (MCP) tool. */
  mcp_name: string | null;
  job_control_options: string[];
  keywords: string[];
  _cited_schemas?: string[];
  _orphan_schemas?: string[];
}

export interface CatalogProcessesResponse {
  summary: {
    total_processes: number;
    by_category: Record<string, number>;
    with_methodology: number;
    with_orphan_schemas: number;
    api_commit_sha: string;
  };
  processes: CatalogProcess[];
}

export async function fetchCatalogDatasets(): Promise<CatalogDatasetsResponse> {
  return fetchJson<CatalogDatasetsResponse>("/catalog/datasets");
}

export async function fetchCatalogProcesses(): Promise<CatalogProcessesResponse> {
  return fetchJson<CatalogProcessesResponse>("/catalog/processes");
}
